import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createChart, CandlestickSeries, LineSeries, AreaSeries } from 'lightweight-charts';
import { useSearchParams } from 'react-router-dom';
import { getInstruments, getCandles, createOrder, getPositions, closePosition, getAccounts, getWatchlist, putWatchlist, addToWatchlist, removeFromWatchlist, resetWatchlist, getWebTraderSettings, getTradeHistory } from '@/utils/api';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WEBTRADER } from '@/constants/testIds';
import { formatMoney, formatMetric } from '@/utils/currency';
import { X, MagnifyingGlass, Plus, Star, ArrowsDownUp } from '@phosphor-icons/react';

const parseErrorDetail = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return error.message || 'Unknown error';
};

// Backend datetimes are UTC; naive ISO strings must not be read as local time.
const parseUtc = (s) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(String(s)) ? s : `${s}Z`);

const AccountMetricsStrip = ({ account }) => {
  if (!account) return null;
  const ccy = account.currency;
  const fm = account.free_margin;
  const unavailable = account.metrics_available === false;
  const items = [
    ['Balance', 'wt-metric-balance', formatMoney(account.balance || 0, ccy), ''],
    ['Equity', 'wt-metric-equity', formatMetric(account.equity === undefined ? 0 : account.equity, ccy), ''],
    ['Margin', 'wt-metric-margin', formatMetric(account.margin === undefined ? 0 : account.margin, ccy), ''],
    ['Free Margin', 'wt-metric-free-margin', formatMetric(fm === undefined ? 0 : fm, ccy), typeof fm === 'number' && fm < 0 ? 'text-trading-bearish' : ''],
    ['Level', 'wt-metric-margin-level', account.margin_level != null ? `${Number(account.margin_level).toFixed(2)}%` : '—', ''],
  ];
  return (
    <div className="ml-auto flex items-center gap-4 pr-3 text-xs" data-testid="wt-account-metrics">
      {unavailable && (
        <span data-testid="wt-metrics-unavailable" className="whitespace-nowrap px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
          Market data unavailable, trading paused
        </span>
      )}
      {items.map(([label, id, value, cls]) => (
        <span key={id} className="whitespace-nowrap">
          <span className="text-slate-500">{label}:</span>{' '}
          <span data-testid={id} className={`font-semibold tabular-nums ${cls}`}>{value}</span>
        </span>
      ))}
    </div>
  );
};


const WebTrader = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const indicatorSeriesRef = useRef({});
  const [instruments, setInstruments] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(searchParams.get('symbol') || 'EURUSD');
  const [selectedInterval, setSelectedInterval] = useState('1H');
  const [quotes, setQuotes] = useState({});
  const [positions, setPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [wlSearch, setWlSearch] = useState('');
  const [wlCategory, setWlCategory] = useState('ALL');
  const [wlSort, setWlSort] = useState('name');
  const [wtSettings, setWtSettings] = useState(null);
  const [chartType, setChartType] = useState('candlestick');
  const [activeIndicators, setActiveIndicators] = useState([]);
  const [activeDrawingTool, setActiveDrawingTool] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartHasData, setChartHasData] = useState(false);
  const [chartMeta, setChartMeta] = useState(null);
  // Trade history (closed positions, server-persisted, per-user)
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Centralized history loader so mount, tab-click and post-close all share one path.
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await getTradeHistory();
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.debug('history load failed', e?.message);
      setHistory([]);
    } finally { setHistoryLoading(false); }
  };
  // Monotonically-incrementing request id so an in-flight response for a
  // symbol/interval the user has since switched away from is discarded.
  const chartReqIdRef = useRef(0);
  // Admin inline price controls
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminForm, setAdminForm] = useState({ price_mode: 'AUTO', manual_bid: '', manual_ask: '', manual_spread: '', price_offset: '', trading_enabled: true });
  const [adminSaving, setAdminSaving] = useState(false);

  const [orderForm, setOrderForm] = useState({
    direction: 'BUY',
    volume: 0.01,
    orderType: 'MARKET',
    stopLoss: '',
    takeProfit: '',
  });

  // Derive effective settings based on the primary account type (DEMO vs live)
  const isDemoAccount = accounts.length > 0 && accounts[0].account_type === 'DEMO';
  const scope = useMemo(() => {
    if (!wtSettings) return null;
    return isDemoAccount ? { ...wtSettings, ...(wtSettings.demo_settings || {}) } : wtSettings;
  }, [wtSettings, isDemoAccount]);

  const enabledTimeframes = scope?.enabled_timeframes || ['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W'];
  const enabledOrderTypes = scope?.enabled_order_types || ['MARKET', 'LIMIT', 'STOP', 'STOP_LOSS', 'TAKE_PROFIT'];
  const enabledIndicators = scope?.enabled_indicators || [];
  const enabledDrawingTools = scope?.enabled_drawing_tools || [];
  const availableChartTypes = scope?.available_chart_types || ['candlestick', 'line', 'area'];

  const INDICATOR_LABELS = { RSI: 'RSI', MACD: 'MACD', BOLLINGER: 'BB', STOCHASTIC: 'STOCH', ATR: 'ATR', VOLUME: 'VOL', SMA: 'SMA', EMA: 'EMA' };
  const DRAWING_LABELS = { FIBONACCI: 'Fib', TREND_LINE: 'Trend', HORIZONTAL_LINE: 'H-Line', VERTICAL_LINE: 'V-Line' };
  const ORDER_LABELS = { MARKET: 'Market', LIMIT: 'Limit', STOP: 'Stop', STOP_LOSS: 'Stop Loss', TAKE_PROFIT: 'Take Profit' };

  const loadInitialData = async () => {
    try {
      const [instrumentsRes, positionsRes, accountsRes, watchlistRes, settingsRes] = await Promise.all([
        getInstruments(), getPositions(), getAccounts(), getWatchlist().catch(() => ({ data: { symbols: [] } })),
        getWebTraderSettings().catch(() => ({ data: null })),
      ]);
      setInstruments(instrumentsRes.data);
      setPositions(positionsRes.data);
      setAccounts(accountsRes.data);
      if (settingsRes?.data) {
        setWtSettings(settingsRes.data);
        const isDemo = accountsRes.data.length > 0 && accountsRes.data[0].account_type === 'DEMO';
        const effective = isDemo ? { ...settingsRes.data, ...(settingsRes.data.demo_settings || {}) } : settingsRes.data;
        const tfList = effective.enabled_timeframes || [];
        const defTf = effective.default_timeframe;
        if (defTf && tfList.includes(defTf)) {
          setSelectedInterval(defTf);
        } else if (tfList.length > 0) {
          setSelectedInterval(tfList[0]);
        }
        const chartTypes = effective.available_chart_types || [];
        const defCt = effective.default_chart_type;
        if (defCt && chartTypes.includes(defCt)) setChartType(defCt);
        else if (chartTypes.length > 0) setChartType(chartTypes[0]);

        // Reset order type if disabled
        const eot = effective.enabled_order_types || [];
        setOrderForm(prev => (eot.includes(prev.orderType) ? prev : { ...prev, orderType: eot[0] || 'MARKET' }));
      }
      const savedSymbols = watchlistRes?.data?.symbols || [];
      if (savedSymbols.length === 0 && instrumentsRes.data.length > 0) {
        // On first visit / after reset, seed the watchlist with EVERY active
        // instrument so the full approved universe is immediately visible,
        // grouped by asset class. Users can then prune manually.
        const defaults = instrumentsRes.data.map(inst => inst.symbol);
        setWatchlist(defaults);
        // Persist so it survives across sessions
        try { await putWatchlist(defaults); } catch (_) { /* ignore */ }
      } else {
        setWatchlist(savedSymbols);
      }
    } catch (error) {
      console.error('Failed to load data', error);
      toast.error('Failed to load trading data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSymbol = async (symbol) => {
    if (watchlist.includes(symbol)) return;
    try {
      const res = await addToWatchlist(symbol);
      setWatchlist(res.data.symbols || [...watchlist, symbol]);
      toast.success(`${symbol} added to watchlist`);
    } catch (e) {
      toast.error('Failed to add to watchlist');
    }
  };

  const handleRemoveSymbol = async (symbol) => {
    try {
      const res = await removeFromWatchlist(symbol);
      setWatchlist(res.data.symbols || watchlist.filter(s => s !== symbol));
      toast.success(`${symbol} removed`);
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  useEffect(() => { loadInitialData(); loadHistory(); }, []);

  // Live-refresh open positions AND account state so P/L, Balance, Equity,
  // Free Margin and any admin edits (open_price / volume / direction / SL /
  // TP) land on the client within ~5 s without a manual reload. Uses the
  // existing shared backend cache — no extra Twelve Data spend per client.
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const [pRes, aRes] = await Promise.all([getPositions(), getAccounts()]);
        if (cancelled) return;
        setPositions(pRes.data || []);
        setAccounts(aRes.data || []);
      } catch (_e) { /* keep last-known state on transient errors */ }
    };
    const id = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [loading]);

  // Prefill quotes ONCE from the shared backend cache so the UI is populated
  // immediately. After the initial prefill, ALL live updates flow through
  // the WebSocket (see below) — no REST polling loop. This eliminates the
  // duplicate quote-refresh pathway that used to double-count against the
  // Twelve Data budget.
  useEffect(() => {
    if (loading || instruments.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        // Only prefill the SAVED watchlist + selected symbol — not the full
        // 60-instrument universe. Everything else stays quiet until the user
        // adds it to their watchlist.
        const scoped = Array.from(new Set([
          selectedSymbol,
          ...(watchlist || []),
        ])).filter(Boolean).join(',');
        if (!scoped) return;
        const r = await axios.get(`${API}/market/quotes?symbols=${encodeURIComponent(scoped)}`);
        if (cancelled) return;
        setQuotes(prev => {
          const next = { ...prev };
          for (const q of r.data || []) next[q.symbol] = q;
          return next;
        });
      } catch (e) { console.debug('quote prefill skipped', e?.message); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, instruments.length, watchlist.length]);

  // When admin panel opens, hydrate form from the currently-selected instrument.
  useEffect(() => {
    if (!showAdminPanel) return;
    const inst = instruments.find(i => i.symbol === selectedSymbol);
    if (!inst) return;
    setAdminForm({
      price_mode: inst.price_mode || 'AUTO',
      manual_bid: inst.manual_bid ?? '',
      manual_ask: inst.manual_ask ?? '',
      manual_spread: inst.manual_spread ?? '',
      price_offset: inst.price_offset ?? '',
      trading_enabled: inst.trading_enabled !== false,
    });
  }, [showAdminPanel, selectedSymbol, instruments]);

  const saveAdminPricing = async () => {
    const inst = instruments.find(i => i.symbol === selectedSymbol);
    if (!inst) return;
    setAdminSaving(true);
    try {
      const payload = {
        price_mode: adminForm.price_mode,
        manual_bid: adminForm.manual_bid === '' ? null : Number(adminForm.manual_bid),
        manual_ask: adminForm.manual_ask === '' ? null : Number(adminForm.manual_ask),
        manual_spread: adminForm.manual_spread === '' ? null : Number(adminForm.manual_spread),
        price_offset: adminForm.price_offset === '' ? null : Number(adminForm.price_offset),
        trading_enabled: !!adminForm.trading_enabled,
      };
      const r = await axios.put(`${API}/admin/instruments/${inst.id}`, payload);
      setInstruments(prev => prev.map(x => x.id === inst.id ? { ...x, ...r.data.instrument } : x));
      // Immediately refresh the quote for this symbol so it reflects the new state.
      const q = await axios.get(`${API}/market/quotes?symbols=${encodeURIComponent(selectedSymbol)}`);
      setQuotes(prev => ({ ...prev, ...(q.data?.[0] ? { [selectedSymbol]: q.data[0] } : {}) }));
      toast.success('Instrument price control saved. Clients see the change now.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setAdminSaving(false); }
  };

  // Single WebSocket for live quote streaming. Subscribes ONLY to the user's
  // saved watchlist + the currently-selected chart symbol — never the full
  // 60-instrument universe. This keeps the Twelve Data budget bounded to
  // the symbols the user is actually looking at, and lets us route ALL
  // clients through the shared MongoDB quote cache on the backend.
  const wsRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    let ws = null;
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https', 'wss');
      ws = new WebSocket(`${wsUrl}/api/ws/market/quotes`);
      wsRef.current = ws;
      const scoped = Array.from(new Set([selectedSymbol, ...(watchlist || [])])).filter(Boolean);
      ws.onopen = () => {
        try { ws.send(JSON.stringify({ symbols: scoped.length ? scoped : ['EURUSD'] })); } catch (_) {}
      };
      ws.onmessage = (event) => {
        const quote = JSON.parse(event.data);
        setQuotes(prev => ({ ...prev, [quote.symbol]: quote }));
      };
    } catch (e) { console.error('WS failed', e); }
    return () => { try { ws?.close(); } catch (_) {} wsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // When the watchlist or the selected symbol changes, push an updated
  // subscription over the already-open WS so we don't tear it down and
  // re-open (which would drop live quotes for a second).
  useEffect(() => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    const scoped = Array.from(new Set([selectedSymbol, ...(watchlist || [])])).filter(Boolean);
    try { wsRef.current.send(JSON.stringify({ symbols: scoped })); } catch (_) {}
  }, [selectedSymbol, watchlist]);

  useEffect(() => {
    if (loading || !chartContainerRef.current) return;
    loadChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, selectedInterval, loading, chartType, activeIndicators]);

  // Auto-retry when a chart-open lands on a cold cache row. The backend
  // fetches on-demand from Twelve Data (~200–300 ms per new symbol/timeframe),
  // so a very short retry cadence heals the "Warming market data" state
  // without hammering Twelve Data — dedupe lives on the backend so N
  // concurrent retries share ONE provider call.
  useEffect(() => {
    if (chartLoading) return;
    if (chartHasData) return;
    const t = setTimeout(() => { loadChart(); }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartLoading, chartHasData, selectedSymbol, selectedInterval]);

  // Keep the LAST (live) candle in sync with the current quote so the chart's
  // current-price line, chart header, order interface and Watchlist all show
  // the SAME price — including when Admin is in MANUAL mode. Historical
  // candles are never rewritten (we only touch the last candle via
  // `series.update()` which mutates just that bar).
  const lastCandleTimeRef = useRef(null);
  useEffect(() => {
    const q = quotes[selectedSymbol];
    if (!q || q.unavailable || q.bid == null || q.ask == null) return;
    const mid = (Number(q.bid) + Number(q.ask)) / 2;
    const t = lastCandleTimeRef.current;
    if (!seriesRef.current || t == null) return;
    try {
      if (chartType === 'line' || chartType === 'area') {
        seriesRef.current.update({ time: t, value: mid });
      } else {
        seriesRef.current.update({ time: t, open: mid, high: mid, low: mid, close: mid });
      }
    } catch (_) { /* series may have been recreated */ }
  }, [quotes, selectedSymbol, chartType]);

  const loadChart = async () => {
    // Race-safe: capture this request's generation. Any response from a
    // previously-selected symbol/interval that lands AFTER a newer request
    // is silently dropped, so the chart never gets overwritten by stale
    // async data.
    const myReqId = ++chartReqIdRef.current;
    const mySymbol = selectedSymbol;
    const myInterval = selectedInterval;
    setChartLoading(true);
    try {
      const now = new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const response = await getCandles(
        mySymbol, 'FOREX', myInterval,
        start.toISOString().split('T')[0],
        now.toISOString().split('T')[0]
      );

      // Drop this response if the user has since switched symbol/interval.
      if (myReqId !== chartReqIdRef.current) return;
      if (mySymbol !== selectedSymbol || myInterval !== selectedInterval) return;

      if (!chartContainerRef.current) return;

      // Clean up previous chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      chartRef.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight || 400,
        localization: { locale: 'en-US' },
        layout: {
          background: { color: '#FFFFFF' },
          textColor: '#0F172A',
        },
        grid: {
          vertLines: { color: '#E2E8F0' },
          horzLines: { color: '#E2E8F0' },
        },
        timeScale: { borderColor: '#E2E8F0', timeVisible: true },
        rightPriceScale: { borderColor: '#E2E8F0' },
      });

      // Add series based on selected chart type
      indicatorSeriesRef.current = {};
      if (chartType === 'line') {
        seriesRef.current = chartRef.current.addSeries(LineSeries, {
          color: '#0EA5E9', lineWidth: 2,
        });
      } else if (chartType === 'area') {
        seriesRef.current = chartRef.current.addSeries(AreaSeries, {
          topColor: 'rgba(14,165,233,0.4)', bottomColor: 'rgba(14,165,233,0.0)',
          lineColor: '#0EA5E9', lineWidth: 2,
        });
      } else {
        seriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
          upColor: '#059669', downColor: '#DC2626',
          borderUpColor: '#059669', borderDownColor: '#DC2626',
          wickUpColor: '#059669', wickDownColor: '#DC2626',
        });
      }

      setChartMeta(response.data.meta || null);

      // Use the ACTUAL candle timestamps returned by the backend — no
      // per-index offset hack. Lightweight-charts requires unique ascending
      // integer seconds; we dedupe by second-precision timestamps (last
      // write wins) and sort ASC.
      const chartData = (response.data.data || []).map((candle) => ({
        time: Math.floor(new Date(candle.time).getTime() / 1000),
        open: candle.open, high: candle.high, low: candle.low, close: candle.close,
      })).sort((a, b) => a.time - b.time);

      // Deduplicate by timestamp (keep the last occurrence, most recent).
      const byTime = new Map();
      for (const d of chartData) byTime.set(d.time, d);
      const uniqueData = Array.from(byTime.values()).sort((a, b) => a.time - b.time);

      if (chartType === 'line' || chartType === 'area') {
        seriesRef.current.setData(uniqueData.map(d => ({ time: d.time, value: d.close })));
      } else {
        seriesRef.current.setData(uniqueData);
      }
      setChartHasData(uniqueData.length > 0);
      // Remember the last bar's timestamp so the live-quote effect can
      // update just that bar rather than rewriting history.
      lastCandleTimeRef.current = uniqueData.length ? uniqueData[uniqueData.length - 1].time : null;

      // Apply active indicator overlays (SMA / EMA on price)
      const applyOverlay = (name, period, color) => {
        if (!activeIndicators.includes(name)) return;
        const values = [];
        if (name === 'SMA') {
          for (let i = 0; i < uniqueData.length; i++) {
            if (i < period - 1) continue;
            let sum = 0;
            for (let j = i - period + 1; j <= i; j++) sum += uniqueData[j].close;
            values.push({ time: uniqueData[i].time, value: sum / period });
          }
        } else if (name === 'EMA') {
          const k = 2 / (period + 1);
          let ema = null;
          for (let i = 0; i < uniqueData.length; i++) {
            ema = ema == null ? uniqueData[i].close : (uniqueData[i].close * k + ema * (1 - k));
            if (i >= period - 1) values.push({ time: uniqueData[i].time, value: ema });
          }
        }
        const s = chartRef.current.addSeries(LineSeries, { color, lineWidth: 1 });
        s.setData(values);
        indicatorSeriesRef.current[name] = s;
      };
      applyOverlay('SMA', 20, '#F59E0B');
      applyOverlay('EMA', 20, '#8B5CF6');

      chartRef.current.timeScale().fitContent();
    } catch (error) {
      console.error('Chart load error:', error);
      if (myReqId === chartReqIdRef.current) {
        setChartHasData(false);
        setChartMeta(null);
      }
    } finally {
      if (myReqId === chartReqIdRef.current) setChartLoading(false);
    }
  };

  // Human-readable "N minutes ago" for the stale banner.
  const formatRelTime = (iso) => {
    if (!iso) return '';
    try {
      const t = new Date(iso).getTime();
      const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
      if (s < 60) return `${s}s ago`;
      const m = Math.floor(s / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 48) return `${h}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch (_) { return ''; }
  };

  const handlePlaceOrder = async () => {
    if (accounts.length === 0) {
      toast.error('No trading account available');
      return;
    }
    if (!orderForm.volume || parseFloat(orderForm.volume) <= 0) {
      toast.error('Volume must be positive');
      return;
    }
    try {
      const quote = quotes[selectedSymbol] || {};
      const currentPrice = orderForm.direction === 'BUY' ? quote.ask : quote.bid;

      await createOrder({
        account_id: accounts[0].id,
        symbol: selectedSymbol,
        order_type: orderForm.orderType,
        direction: orderForm.direction,
        volume: parseFloat(orderForm.volume),
        entry_price: orderForm.orderType === 'MARKET' ? null : (currentPrice || null),
        stop_loss: orderForm.stopLoss ? parseFloat(orderForm.stopLoss) : null,
        take_profit: orderForm.takeProfit ? parseFloat(orderForm.takeProfit) : null,
      });

      toast.success('Order placed successfully!');
      loadInitialData();
      setOrderForm({ ...orderForm, stopLoss: '', takeProfit: '' });
    } catch (error) {
      toast.error(parseErrorDetail(error));
    }
  };

  const handleClosePosition = async (positionId) => {
    try {
      await closePosition(positionId);
      toast.success('Position closed successfully!');
      loadInitialData();
      loadHistory();
    } catch (error) {
      toast.error(parseErrorDetail(error));
    }
  };

  const currentQuote = quotes[selectedSymbol] || { bid: null, ask: null, spread: null, unavailable: true };
  const isQuoteUnavailable = currentQuote.unavailable || (currentQuote.bid == null && currentQuote.ask == null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div data-testid={WEBTRADER.container} className="webtrader-container grid grid-cols-12 grid-rows-6 h-screen gap-1 p-1 bg-slate-200">
      <div data-testid={WEBTRADER.watchlist} className="col-span-2 row-span-6 bg-white border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between gap-2">
          <h3 className="font-bold text-sm uppercase tracking-wider text-slate-700">Watchlist</h3>
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                if (!window.confirm('Reset watchlist to the latest defaults (approx 40 symbols across every category)?')) return;
                try {
                  await resetWatchlist();
                  await loadInitialData();
                  toast.success('Watchlist reset to defaults');
                } catch (e) {
                  toast.error('Failed to reset watchlist');
                }
              }}
              data-testid="watchlist-reset"
              className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-brand-accent text-[10px] font-semibold uppercase"
              title="Reset to defaults"
            >Reset</button>
            <button onClick={() => setShowAddPanel(v => !v)} data-testid="watchlist-add-toggle" className="p-1 rounded hover:bg-slate-100 text-brand-accent" title="Add symbol">
              <Plus size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className="p-2 border-b border-slate-200 space-y-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search symbol…"
              value={wlSearch}
              onChange={e => setWlSearch(e.target.value)}
              data-testid="watchlist-search"
              className="w-full pl-6 pr-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded focus:outline-none focus:border-brand-accent"
            />
          </div>
          <div className="flex gap-1 text-[10px]">
            <select value={wlCategory} onChange={e=>setWlCategory(e.target.value)} data-testid="watchlist-category" className="flex-1 bg-slate-50 border border-slate-200 rounded px-1 py-0.5">
              <option value="ALL">All</option>
              <option value="FOREX">Forex</option>
              <option value="CRYPTO">Crypto</option>
              <option value="METALS">Metals</option>
              <option value="INDICES">Indices</option>
              <option value="ENERGY">Energy</option>
              <option value="SHARES">Shares</option>
              <option value="ETFS">ETFs</option>
              <option value="FUTURES">Futures</option>
            </select>
            <select value={wlSort} onChange={e=>setWlSort(e.target.value)} data-testid="watchlist-sort" className="flex-1 bg-slate-50 border border-slate-200 rounded px-1 py-0.5">
              <option value="name">Name</option>
              <option value="price">Price</option>
              <option value="change">Change %</option>
              <option value="category">Category</option>
            </select>
          </div>
        </div>
        {showAddPanel && (
          <div className="p-2 border-b border-slate-200 bg-blue-50/40 max-h-40 overflow-y-auto" data-testid="watchlist-add-panel">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Add to watchlist</div>
            {instruments.filter(i => !watchlist.includes(i.symbol) && (!wlSearch || i.symbol.toLowerCase().includes(wlSearch.toLowerCase()))).slice(0, 60).map(i => (
              <button key={i.symbol} onClick={() => handleAddSymbol(i.symbol)} data-testid={`watchlist-add-${i.symbol}`} className="w-full flex items-center justify-between text-xs py-1 px-2 hover:bg-white rounded">
                <span className="font-semibold">{i.symbol}</span>
                <span className="text-[9px] text-slate-400 uppercase">{i.asset_class}</span>
              </button>
            ))}
            {instruments.filter(i => !watchlist.includes(i.symbol)).length === 0 && (
              <div className="text-xs text-slate-500 text-center py-2">All instruments already on your watchlist.</div>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {(() => {
            const list = instruments
              .filter(i => watchlist.includes(i.symbol))
              .filter(i => !wlSearch || i.symbol.toLowerCase().includes(wlSearch.toLowerCase()))
              .filter(i => wlCategory === 'ALL' || (i.asset_class || '').toUpperCase() === wlCategory);
            const sorted = [...list].sort((a, b) => {
              const qa = quotes[a.symbol] || {}; const qb = quotes[b.symbol] || {};
              if (wlSort === 'price') return (qb.bid || 0) - (qa.bid || 0);
              if (wlSort === 'change') return (qb.change_percent || 0) - (qa.change_percent || 0);
              if (wlSort === 'category') return (a.asset_class || '').localeCompare(b.asset_class || '');
              return a.symbol.localeCompare(b.symbol);
            });
            if (sorted.length === 0) return <div className="p-4 text-center text-xs text-slate-500">No symbols. Click + to add.</div>;
            // Group by asset_class when sort=category OR always show category header per group
            const groups = {};
            sorted.forEach(i => { const k = i.asset_class || 'OTHER'; (groups[k] = groups[k] || []).push(i); });
            return Object.entries(groups).map(([cat, list]) => (
              <div key={cat} data-testid={`watchlist-group-${cat}`}>
                <div className="px-3 py-1 bg-slate-100 border-y border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-bold sticky top-0">{cat} <span className="text-slate-400 font-normal">· {list.length}</span></div>
                {list.map(instrument => {
                  const quote = quotes[instrument.symbol] || {};
                  const isSelected = selectedSymbol === instrument.symbol;
                  const change = quote.change_percent;
                  return (
                    <div
                      key={instrument.symbol}
                      data-testid={`watchlist-${instrument.symbol}`}
                      className={`group px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 ${isSelected ? 'bg-blue-50 border-l-2 border-brand-accent' : ''}`}
                      onClick={() => setSelectedSymbol(instrument.symbol)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm text-brand-primary">{instrument.symbol}</div>
                        <button onClick={(e) => { e.stopPropagation(); handleRemoveSymbol(instrument.symbol); }} data-testid={`watchlist-remove-${instrument.symbol}`} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition">
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-0.5">
                        <div className="text-[10px] text-trading-bearish tabular-nums">{quote.unavailable ? 'N/A' : (quote.bid?.toFixed(5) || '---')}</div>
                        <div className="text-[10px] text-trading-bullish tabular-nums">{quote.unavailable ? 'N/A' : (quote.ask?.toFixed(5) || '---')}</div>
                      </div>
                      {change != null && (
                        <div className="flex justify-end mt-0.5">
                          <span className={`text-[10px] tabular-nums font-semibold ${change >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'}`}>
                            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="col-span-8 row-span-4 bg-white border border-slate-200 flex flex-col">
        <div className="p-2 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-lg font-bold text-brand-primary">{selectedSymbol}</h2>
            {instruments.find(i => i.symbol === selectedSymbol)?.description && (
              <span data-testid="instrument-description" className="text-xs text-slate-500 max-w-md">
                {instruments.find(i => i.symbol === selectedSymbol).name} · {instruments.find(i => i.symbol === selectedSymbol).description}
              </span>
            )}
            {isQuoteUnavailable ? (
              <span data-testid="quote-unavailable" className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Market data unavailable</span>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Bid:</span>
                  <span className="text-sm font-semibold tabular-nums text-trading-bearish" data-testid="chart-bid">{currentQuote.bid?.toFixed(5)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Ask:</span>
                  <span className="text-sm font-semibold tabular-nums text-trading-bullish" data-testid="chart-ask">{currentQuote.ask?.toFixed(5)}</span>
                </div>
                {currentQuote.spread != null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">Spread:</span>
                    <span className="text-sm font-semibold tabular-nums text-slate-800" data-testid="chart-spread">{Number(currentQuote.spread).toFixed(5)}</span>
                  </div>
                )}
                {currentQuote.source === 'manual' && (
                  <span data-testid="price-source-manual" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 uppercase">Manual</span>
                )}
                {currentQuote.source === 'auto' && (
                  <span data-testid="price-source-auto" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase">Live</span>
                )}
              </>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowAdminPanel(v => !v)}
                data-testid="admin-price-toggle"
                className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-700"
                title="Admin price controls"
              >{showAdminPanel ? 'Close Admin' : 'Admin Price Controls'}</button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isDemoAccount && (
              <span data-testid="demo-account-badge" className="px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded">Demo</span>
            )}
            <div className="flex gap-1" data-testid="timeframes-bar">
              {enabledTimeframes.map((interval) => (
                <button
                  key={interval}
                  onClick={() => setSelectedInterval(interval)}
                  data-testid={`timeframe-${interval}`}
                  className={`px-2 py-1 text-xs font-semibold rounded ${
                    selectedInterval === interval ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >{interval}</button>
              ))}
            </div>
            {availableChartTypes.length > 1 && (
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger data-testid="chart-type-select" className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableChartTypes.map(ct => <SelectItem key={ct} value={ct} className="capitalize">{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        {(enabledIndicators.length > 0 || enabledDrawingTools.length > 0) && (
          <div className="px-2 py-1.5 border-b border-slate-200 flex items-center gap-3 flex-wrap bg-slate-50">
            {enabledIndicators.length > 0 && (
              <div className="flex items-center gap-1" data-testid="indicators-bar">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Indicators</span>
                {enabledIndicators.map(ind => {
                  const on = activeIndicators.includes(ind);
                  return (
                    <button
                      key={ind}
                      data-testid={`indicator-${ind}`}
                      onClick={() => setActiveIndicators(prev => prev.includes(ind) ? prev.filter(x => x !== ind) : [...prev, ind])}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${on ? 'bg-brand-accent text-white border-brand-accent' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-accent'}`}
                    >{INDICATOR_LABELS[ind] || ind}</button>
                  );
                })}
              </div>
            )}
            {enabledDrawingTools.length > 0 && (
              <div className="flex items-center gap-1" data-testid="drawing-tools-bar">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Tools</span>
                {enabledDrawingTools.map(dt => {
                  const on = activeDrawingTool === dt;
                  return (
                    <button
                      key={dt}
                      data-testid={`drawing-${dt}`}
                      onClick={() => setActiveDrawingTool(on ? null : dt)}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${on ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-800'}`}
                    >{DRAWING_LABELS[dt] || dt}</button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {isAdmin && showAdminPanel && (
          <div data-testid="admin-price-panel" className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="flex flex-wrap items-end gap-3">
              <div className="text-xs font-bold uppercase text-slate-700 mr-2">Admin · {selectedSymbol}</div>
              <div>
                <label className="text-[10px] uppercase text-slate-500">Mode</label>
                <select
                  value={adminForm.price_mode}
                  onChange={e => setAdminForm({...adminForm, price_mode: e.target.value})}
                  data-testid="admin-price-mode"
                  className="block h-8 text-xs border border-slate-300 rounded px-2"
                >
                  <option value="AUTO">AUTO (Twelve Data)</option>
                  <option value="MANUAL">MANUAL override</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500">Manual Bid</label>
                <Input type="number" step="0.00001" value={adminForm.manual_bid} onChange={e=>setAdminForm({...adminForm, manual_bid: e.target.value})} className="h-8 w-28" data-testid="admin-manual-bid" placeholder="—" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500">Manual Ask</label>
                <Input type="number" step="0.00001" value={adminForm.manual_ask} onChange={e=>setAdminForm({...adminForm, manual_ask: e.target.value})} className="h-8 w-28" data-testid="admin-manual-ask" placeholder="—" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500">Manual Spread</label>
                <Input type="number" step="0.00001" value={adminForm.manual_spread} onChange={e=>setAdminForm({...adminForm, manual_spread: e.target.value})} className="h-8 w-24" data-testid="admin-manual-spread" placeholder="auto" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500">Offset (AUTO)</label>
                <Input type="number" step="0.00001" value={adminForm.price_offset} onChange={e=>setAdminForm({...adminForm, price_offset: e.target.value})} className="h-8 w-24" data-testid="admin-price-offset" placeholder="0" />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input type="checkbox" checked={!!adminForm.trading_enabled} onChange={e=>setAdminForm({...adminForm, trading_enabled: e.target.checked})} data-testid="admin-trading-enabled" id="admin-tradable" />
                <label htmlFor="admin-tradable" className="text-xs">Enabled for trading</label>
              </div>
              <Button size="sm" onClick={saveAdminPricing} disabled={adminSaving} data-testid="admin-price-save">
                {adminSaving ? 'Saving…' : 'Save (applies immediately)'}
              </Button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              AUTO uses shared Twelve Data cache (~1 call per symbol per 90s across all users). Offset adds a fixed number to Bid/Ask.
              MANUAL bypasses the market feed and shows exactly what you enter here.
            </div>
          </div>
        )}
        <div data-testid={WEBTRADER.chartContainer} ref={chartContainerRef} className="flex-1 min-h-0 relative">
          {chartHasData && chartMeta?.stale && (
            <div
              data-testid="chart-stale-banner"
              className="absolute top-2 left-2 z-20 px-2 py-1 rounded bg-amber-100 text-amber-900 text-[11px] font-semibold shadow-sm border border-amber-300"
              title={chartMeta?.rate_limit?.pause_reason || 'Market data provider throttled'}
            >
              Chart last updated {formatRelTime(chartMeta.updated_at)}
              {chartMeta?.rate_limit?.paused ? ' · provider throttled' : ''}
            </div>
          )}
          {chartLoading && (
            <div data-testid="chart-loading-overlay" className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <div className="loader" />
                Loading chart…
              </div>
            </div>
          )}
          {!chartLoading && !chartHasData && (
            <div data-testid="chart-no-data" className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-3 text-slate-500 text-sm max-w-md text-center px-4">
                <span>
                  {chartMeta?.reason === 'no verified provider feed'
                    ? 'Market data unavailable: no verified price feed for this instrument.'
                    : chartMeta?.rate_limit?.paused
                    ? 'Market data provider is currently throttled. No fresh candles are available for this instrument yet.'
                    : 'Warming market data for this chart — it will appear here shortly.'}
                </span>
                {chartMeta?.rate_limit?.pause_reason && (
                  <span className="text-[11px] text-slate-400">{chartMeta.rate_limit.pause_reason}</span>
                )}
                <button onClick={loadChart} className="px-3 py-1.5 text-xs rounded-md bg-brand-primary text-white hover:opacity-90" data-testid="chart-retry-btn">
                  Retry now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div data-testid={WEBTRADER.orderTicket} className="col-span-2 row-span-6 bg-white border border-slate-200 p-4 overflow-y-auto">
        <h3 className="font-bold text-sm uppercase tracking-wider text-slate-700 mb-4">Order Ticket</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Symbol</label>
            <div className="text-lg font-bold text-brand-primary mt-1">{selectedSymbol}</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid={WEBTRADER.buyBtn}
              onClick={() => setOrderForm({ ...orderForm, direction: 'BUY' })}
              className={`h-12 font-bold ${orderForm.direction === 'BUY' ? 'bg-trading-bullish hover:bg-trading-bullish' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
            >BUY</Button>
            <Button
              data-testid={WEBTRADER.sellBtn}
              onClick={() => setOrderForm({ ...orderForm, direction: 'SELL' })}
              className={`h-12 font-bold ${orderForm.direction === 'SELL' ? 'bg-trading-bearish hover:bg-trading-bearish' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
            >SELL</Button>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Volume</label>
            <Input
              data-testid={WEBTRADER.volumeInput}
              type="number" step="0.01" min="0.01"
              value={orderForm.volume}
              onChange={(e) => setOrderForm({ ...orderForm, volume: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase">Order Type</label>
            <Select value={orderForm.orderType} onValueChange={(v) => setOrderForm({ ...orderForm, orderType: v })}>
              <SelectTrigger data-testid="order-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {enabledOrderTypes.filter(t => ['MARKET', 'LIMIT', 'STOP'].includes(t)).map(t => (
                  <SelectItem key={t} value={t} data-testid={`order-type-item-${t}`}>{ORDER_LABELS[t] || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {enabledOrderTypes.includes('STOP_LOSS') && (
            <div data-testid="stop-loss-container">
              <label className="text-xs font-semibold text-slate-600 uppercase">Stop Loss</label>
              <Input
                data-testid="stop-loss-input"
                type="number" step="0.00001"
                value={orderForm.stopLoss}
                onChange={(e) => setOrderForm({ ...orderForm, stopLoss: e.target.value })}
                className="mt-1" placeholder="0.00000"
              />
            </div>
          )}

          {enabledOrderTypes.includes('TAKE_PROFIT') && (
            <div data-testid="take-profit-container">
              <label className="text-xs font-semibold text-slate-600 uppercase">Take Profit</label>
              <Input
                data-testid="take-profit-input"
                type="number" step="0.00001"
                value={orderForm.takeProfit}
                onChange={(e) => setOrderForm({ ...orderForm, takeProfit: e.target.value })}
                className="mt-1" placeholder="0.00000"
              />
            </div>
          )}

          <Button onClick={handlePlaceOrder} data-testid="place-order-btn" disabled={accounts[0]?.metrics_available === false || isQuoteUnavailable} className="w-full h-12 font-bold">
            Place {orderForm.direction} Order
          </Button>
        </div>
      </div>

      <div className="col-span-8 row-span-2 bg-white border border-slate-200 overflow-hidden">
        <Tabs defaultValue="positions" className="h-full flex flex-col" onValueChange={(v) => { if (v === 'history') loadHistory(); }}>
          <TabsList className="w-full justify-start rounded-none border-b border-slate-200">
            <TabsTrigger data-testid={WEBTRADER.positionsTab} value="positions">Open Positions ({positions.length})</TabsTrigger>
            <TabsTrigger value="orders">Pending Orders</TabsTrigger>
            <TabsTrigger value="history" data-testid="trade-history-tab" onClick={loadHistory}>History ({history.length})</TabsTrigger>
            <AccountMetricsStrip account={accounts[0]} />
          </TabsList>

          <TabsContent value="positions" className="flex-1 overflow-auto p-0 m-0">
            {positions.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500">No open positions</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left uppercase tracking-wider text-slate-600">
                    <th className="p-2">Symbol</th>
                    <th className="p-2">Direction</th>
                    <th className="p-2">Volume</th>
                    <th className="p-2 text-right">Open</th>
                    <th className="p-2 text-right">Current</th>
                    <th className="p-2 text-right">P/L</th>
                    <th className="p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position) => (
                    <tr key={position.id} className="position-row">
                      <td className="p-2 font-semibold">{position.symbol}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          position.direction === 'BUY' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'
                        }`}>{position.direction}</span>
                      </td>
                      <td className="p-2 tabular-nums">{position.volume}</td>
                      <td className="p-2 text-right tabular-nums">{position.open_price?.toFixed(5)}</td>
                      <td className="p-2 text-right tabular-nums">{position.current_price?.toFixed(5)}</td>
                      <td className={`p-2 text-right font-semibold tabular-nums ${
                        position.profit_loss >= 0 ? 'profit-positive' : 'profit-negative'
                      }`}>{formatMetric(position.profit_loss, accounts[0]?.currency)}</td>
                      <td className="p-2">
                        <Button
                          data-testid={`close-position-${position.id}`}
                          size="sm" variant="destructive"
                          onClick={() => handleClosePosition(position.id)}
                          className="h-7 px-2"
                        ><X size={14} weight="bold" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TabsContent>
          <TabsContent value="orders" className="flex-1 overflow-auto p-4">
            <div className="text-center text-slate-500">No pending orders</div>
          </TabsContent>
          <TabsContent value="history" className="flex-1 overflow-auto p-0 m-0">
            {historyLoading ? (
              <div className="text-center text-slate-500 p-4">Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-center text-slate-500 p-4">No closed trades yet</div>
            ) : (
              <table className="w-full text-xs" data-testid="trade-history-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left uppercase tracking-wider text-slate-600">
                    <th className="p-2">Symbol</th>
                    <th className="p-2">Direction</th>
                    <th className="p-2">Volume</th>
                    <th className="p-2 text-right">Open</th>
                    <th className="p-2 text-right">Close</th>
                    <th className="p-2">Opened</th>
                    <th className="p-2">Closed</th>
                    <th className="p-2 text-right">P/L</th>
                    <th className="p-2 text-right">Commission</th>
                    <th className="p-2 text-right">Swap</th>
                    <th className="p-2">Close reason</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`history-row-${h.id}`} className="border-b border-slate-100">
                      <td className="p-2 font-semibold">{h.symbol}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          h.direction === 'BUY' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'
                        }`}>{h.direction}</span>
                      </td>
                      <td className="p-2 tabular-nums">{h.volume}</td>
                      <td className="p-2 text-right tabular-nums">{Number(h.open_price || 0).toFixed(5)}</td>
                      <td className="p-2 text-right tabular-nums">{Number(h.close_price ?? h.current_price ?? 0).toFixed(5)}</td>
                      <td className="p-2 text-slate-500">{h.opened_at ? parseUtc(h.opened_at).toLocaleString() : '—'}</td>
                      <td className="p-2 text-slate-500">
                        {h.closed_at ? parseUtc(h.closed_at).toLocaleString() : '—'}
                        {h.effective_closed_at && (
                          <div className="text-xs" data-testid={`history-effective-close-${h.id}`}>
                            Effective: {parseUtc(h.effective_closed_at).toISOString().slice(0, 16).replace('T', ' ')} UTC
                          </div>
                        )}
                      </td>
                      <td className={`p-2 text-right font-semibold tabular-nums ${
                        (h.profit_loss || 0) >= 0 ? 'profit-positive' : 'profit-negative'
                      }`}>{formatMoney(h.profit_loss || 0, h.account_currency || accounts[0]?.currency)}</td>
                      <td className="p-2 text-right tabular-nums">{formatMoney(h.commission || 0, h.account_currency || accounts[0]?.currency)}</td>
                      <td className="p-2 text-right tabular-nums">{formatMoney(h.swap || 0, h.account_currency || accounts[0]?.currency)}</td>
                      <td className="p-2 text-slate-600 text-xs" data-testid={`history-close-reason-${h.id}`}>{h.close_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WebTrader;
